/**
 * Super-admin control to mark one workspace unrestricted.
 * Props: api (API.json), toast(message, type), tenant ({ id, name }).
 */
import { formatDateTime } from './datetime.js';

const { useEffect, useId, useState } = React;

const WARNING = 'Removes plan limits, billing checks, and feature locks for this account. Do-not-call lists, opt-outs, and legal calling hours still apply because they are the law.';

function tenantIdOf(tenant) {
  const raw = tenant && (tenant.id != null ? tenant.id : tenant.tenant_id);
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

export function UnrestrictedAccountToggle({ api, toast, tenant }) {
  const tenantId = tenantIdOf(tenant);
  const reasonId = useId();
  const toggleId = useId();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [draft, setDraft] = useState(false);
  const [reason, setReason] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!tenantId) {
      setEnabled(false);
      setDraft(false);
      setReason('');
      setSavedAt(null);
      setLoadError('');
      return undefined;
    }
    setLoading(true);
    setLoadError('');
    api('GET', '/api/admin/tenants/' + tenantId + '/unrestricted')
      .then((data) => {
        if (cancelled) return;
        const row = data && data.tenant ? data.tenant : {};
        const on = !!row.unrestricted;
        setEnabled(on);
        setDraft(on);
        setReason(row.unrestricted_reason || '');
        setSavedAt(row.unrestricted_at || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(errorMessage(err, 'Could not load the unrestricted setting.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [api, tenantId]);

  function notify(message, type) {
    if (toast) toast(message, type);
  }

  async function save(event) {
    event.preventDefault();
    if (!tenantId || saving) return;
    const text = reason.trim();
    if (draft && !text) {
      notify('Enter a reason before marking this account unrestricted.', 'error');
      return;
    }
    if (text.length > 300) {
      notify('Keep the reason to 300 characters or fewer.', 'error');
      return;
    }
    setSaving(true);
    try {
      const data = await api('POST', '/api/admin/tenants/' + tenantId + '/unrestricted', {
        unrestricted: draft,
        reason: text
      });
      const row = data && data.tenant ? data.tenant : {};
      const on = !!row.unrestricted;
      setEnabled(on);
      setDraft(on);
      setReason(row.unrestricted_reason || '');
      setSavedAt(row.unrestricted_at || null);
      notify(on ? 'This account is now unrestricted.' : 'This account follows its plan again.', 'success');
    } catch (err) {
      notify(errorMessage(err, 'Could not update this account.'), 'error');
    } finally {
      setSaving(false);
    }
  }

  const formattedAt = savedAt ? formatDateTime(savedAt, { style: 'full' }) : '';
  const when = formattedAt && /\d/.test(formattedAt) ? formattedAt : '';
  const name = tenant && tenant.name ? tenant.name : 'This account';

  return (
    <form className="acct-section ao-panel" onSubmit={save}>
      <div className="acct-title">Unrestricted account</div>
      <p className="legal-banner ao-warning" role="note">{WARNING}</p>
      {!tenantId ? (
        <p className="acct-help">Open a workspace to change this.</p>
      ) : loading ? (
        <p className="acct-help">Loading…</p>
      ) : (
        <div className="acct-form">
          {loadError ? <p className="acct-help" role="alert">{loadError}</p> : null}
          <p className="acct-help">
            {enabled
              ? name + ' is unrestricted' + (when ? ' as of ' + when : '') + '.'
              : name + ' follows its plan, billing, and feature locks.'}
          </p>
          <label className="ca-check ao-row" htmlFor={toggleId}>
            <input
              id={toggleId}
              type="checkbox"
              checked={draft}
              disabled={saving}
              onChange={(event) => setDraft(event.target.checked)}
            />
            <span>Unrestricted</span>
          </label>
          <label className="acct-label" htmlFor={reasonId}>
            Reason {draft ? '(required)' : '(optional)'}
            <textarea
              id={reasonId}
              className="acct-input"
              rows={3}
              maxLength={300}
              value={reason}
              disabled={saving}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Why this account should skip plan and billing limits"
            />
          </label>
          <div className="ao-actions">
            <button type="submit" className="button primary small" disabled={saving || !!loadError}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
